import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal"
import RadioButton from "components/radioButton";
import { IMultilingualContent, ISimpleEventDetail, PriceType, SupportedLanguage } from "interfaces";
import { get } from "lodash";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { languageSchema } from "routes/event/schema";
import { PRICE_TYPES } from "utills/constants";
import { getDefaultEventLanguageValue } from "utills/helpers";
import { z } from "zod";

interface IDuplicateTicketModalProps extends IModalProps {
  onSubmit: (data: any) => void;
  eventDetailData: ISimpleEventDetail;
}

const DuplicateTicketSchema = (languages: SupportedLanguage[]) => {
  return z.discriminatedUnion("price_type",[
    z.object({
      price_type: z.literal(PriceType.FREE),
      category: languageSchema(languages),
      original_price: z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      price: z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      }),
    z.object({
      price_type: z.literal(PriceType.PAID),
      category: languageSchema(languages),
      original_price: z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" }),
      price: z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
    }),
  ])
  .refine((data) => data.original_price >= data.price, {
    message: "Original price cannot be smaller than sell price.",
    path: ["original_price"],
  })
};

interface IAddTicketForm {
  category: IMultilingualContent;
  price_type: PriceType;
  original_price: number;
  price: number;
}

export const DuplicateTicketModal = ({onClose, open, onSubmit, eventDetailData}: IDuplicateTicketModalProps) => {

  const {
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IAddTicketForm>({
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        DuplicateTicketSchema(eventDetailData.languages)
      );
      return createResolver(values, context, options);
    },    
  });

  useEffect(() => {
    if (open) {
      reset({
        category: getDefaultEventLanguageValue(eventDetailData.languages),
        original_price: 0,
        price: 0,
        price_type: PriceType.PAID
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])
  
  const _submit = (values: IAddTicketForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };
  
  return (
    <Modal title="Please enter the new info when duplicating" onClose={onClose} open={open} closeOnClickBackdrop={false}>
      <div className="flex flex-col gap-y-4 box-container w-full">
        <form onSubmit={handleSubmit(_submit)} className="grid sm:grid-cols-6 gap-y-4 gap-x-4">
          <div className="col-span-full">
            <Label
              required
              id={`category_${eventDetailData.languages[0] ?? "en"}`}
              name="Category"
            />
            <div className="flex flex-col gap-y-4">
              {eventDetailData.languages.map((lang) => (
                <div key={lang}>
                  <div className="relative" key={lang}>
                    <Input
                      id={`category_${lang}`}
                      type="text"
                      className="pr-11"
                      placeholder={`Cateogry (${lang})`}
                      {...register(`category.${lang}`)}
                      error={get(
                        errors,
                        `category.${lang}.message`,
                        ""
                      )}
                    />
                    <img
                      src={`/images/flags/circle-${lang}.png`}
                      alt="Language Icon"
                      className="absolute top-1/2 right-3 -translate-y-1/2"
                    />
                  </div>
                  {get(
                    errors,
                    `category.${lang}`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `category.${lang}.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <div className="flex flex-row gap-x-4">
              <div>
                <Controller
                  control={control}
                  name="price_type"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <div className="flex flex-row flex-wrap gap-x-3">
                        {PRICE_TYPES.map(type => (
                          <RadioButton
                            key={type.value}
                            checked={type.value === value}
                            onChange={() => {
                              onChange(type.value);
                              if (type.value === PriceType.FREE) {
                                setValue("price", 0, {shouldValidate: true});
                                setValue("original_price", 0, {shouldValidate: true});
                              }     
                            }}
                            item={{
                              id: type.value,
                              title: type.label,
                            }}
                          />
                        ))}
                      </div>
                    );
                  }}
                />
                {get(errors, `price_type`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `price_type.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
          <div className="sm:col-span-3">
            <Label
              id="original_price"
              name="Original price"
              required
            />
            <div className="flex">
              <Input
                id="original_price"
                type="number"
                decimal={2}
                className="rounded-r-none"
                placeholder="Original price"
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "original_price")}
                {...register("original_price", {
                  valueAsNumber: true
                })}  
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(
              errors,
              `original_price`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `original_price.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>

          <div className="sm:col-span-3">
            <Label
              id="price"
              name="Sell price"
              required
            />
            <div className="flex">
              <Input
                id="price"
                type="number"
                decimal={2}
                className="rounded-r-none"
                placeholder="Sell price"
                disabled={watch("price_type") === PriceType.FREE}
                error={get(errors, "price")}
                {...register("price", {
                  valueAsNumber: true
                })}  
              />
              <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                {eventDetailData.currency}
              </span>
            </div>
            {get(
              errors,
              `price`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `price.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
          <div className="col-span-full flex items-center gap-x-4 mt-4">
            <div className="flex-1">
              <Button
                type="button"
                data-autofocus
                onClick={onClose}
                className="inline-flex w-full justify-center bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300"
              >
                Cancel
              </Button>
            </div>
            <div className="flex-1">
              <Button type="submit" className="w-full">
                Duplicate
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}